import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserContext } from '@/auth/UserContext';
import { DetailLayout } from '@/components/ProfileMenu/DetailLayout';
import { useUpdateEmail } from '@/components/ProfileMenu/hooks/useUpdateEmail';
import { Icon } from '@/components/Icons';
import {
  ErrorMessage,
  getResponseErrorCode,
  getResponseErrorType,
  logRequestError,
} from '@/utils/errors';
import { submitOnEnter } from '@/utils/form';
import { personTypeIcons } from '@/utils/person';
import { isValidEmail } from '@/utils/validation';

const EMAIL_ALREADY_REGISTERED_CODE = 'EmailAlreadyRegistered';
const VALIDATION_ERROR_TYPE = 'ValidationError';

enum FieldError {
  Invalid = 'invalid',
  AlreadyInUse = 'alreadyInUse',
}

interface Props {
  onBack: () => void;
  onClose: () => void;
  onSuccess: () => void;
  onError: () => void;
}

export const EmailView: React.FC<Props> = ({
  onBack,
  onClose,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const { user, userDisplayName } = useUserContext();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<FieldError | null>(null);

  const isEmpty = email.length === 0;

  const { updateEmail, isPending } = useUpdateEmail();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);

    if (error) {
      setError(null);
    }
  };

  const handleSave = async () => {
    if (isPending || isEmpty) {
      return;
    }

    if (!isValidEmail(email)) {
      setError(FieldError.Invalid);
      return;
    }

    setError(null);

    try {
      await updateEmail(email);
      onSuccess();
    } catch (requestError) {
      if (
        getResponseErrorCode(requestError) === EMAIL_ALREADY_REGISTERED_CODE
      ) {
        setError(FieldError.AlreadyInUse);
        return;
      }

      if (getResponseErrorType(requestError) === VALIDATION_ERROR_TYPE) {
        setError(FieldError.Invalid);
        return;
      }

      logRequestError(ErrorMessage.ChangeEmail, requestError);
      onError();
    }
  };

  return (
    <DetailLayout
      title={t('changeEmail.title')}
      isSaveDisabled={isEmpty}
      isSaving={isPending}
      onBack={onBack}
      onClose={onClose}
      onSave={handleSave}
    >
      <div className="mt-4 mb-5 flex items-center gap-3 border-b border-divider pb-5 md:mt-0 md:border-border">
        {user?.personType && (
          <Icon
            icon={personTypeIcons[user.personType]}
            className="size-10 shrink-0"
          />
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-body-16 text-foreground capitalize">
            {userDisplayName}
          </p>
          <p className="truncate text-body-14 text-muted-foreground md:text-caption-12">
            {user?.emailAddress}
          </p>
        </div>
      </div>
      <label className="mb-2 text-body-14 text-foreground md:text-body-16">
        {t('changeEmail.label')}
      </label>
      <input
        type="email"
        inputMode="email"
        autoComplete="off"
        value={email}
        onChange={handleChange}
        onKeyDown={submitOnEnter(handleSave)}
        placeholder={t('changeEmail.placeholder')}
        className="h-14 rounded-xl border border-transparent bg-on-primary-surface px-4 text-body-16 text-foreground outline-none placeholder:text-subtle-foreground md:h-12"
      />
      {error === FieldError.Invalid && (
        <p className="mt-2 text-body-14 text-destructive">
          {t('changeEmail.invalidFormatError')}
        </p>
      )}
      {error === FieldError.AlreadyInUse && (
        <p className="mt-2 text-body-14 text-destructive">
          {t('changeEmail.alreadyInUseError')}
        </p>
      )}
    </DetailLayout>
  );
};
